import React, { useEffect, useState } from "react";
import { FaSearch, FaExclamationTriangle, FaCheckCircle } from "react-icons/fa";
import { useAppTheme } from "../context/AppThemeContext";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";

export default function TeacherStudentUsageView() {
  const { isDarkMode } = useAppTheme();
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [activeView, setActiveView] = useState("activity");

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await api.get(API_PATHS.usage.teacherStudents);

        if (response.ok) {
          setStudents(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error("Error fetching student usage:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
    const interval = setInterval(fetchStudents, 300000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let filtered = students.filter((s) =>
      `${s.firstName || ""} ${s.lastName || ""}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (filterStatus !== "all") {
      filtered = filtered.filter((s) => s.usageStatus === filterStatus);
    }

    setFilteredStudents(filtered);
  }, [students, searchTerm, filterStatus]);

  const getStatusBadge = (status) => {
    const config = {
      active: { bg: "bg-green-950/40", border: "border-green-800/50", text: "text-green-400", icon: FaCheckCircle, label: "Active" },
      inactive: { bg: "bg-yellow-950/40", border: "border-yellow-800/50", text: "text-yellow-400", icon: FaExclamationTriangle, label: "Inactive" },
      unused: { bg: "bg-red-950/40", border: "border-red-800/50", text: "text-red-400", icon: FaExclamationTriangle, label: "Unused" },
    };
    const cfg = config[status] || config.inactive;
    const Icon = cfg.icon;

    return (
      <span className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-semibold ${cfg.bg} ${cfg.border} ${cfg.text}`}>
        <Icon className="text-[10px]" /> {cfg.label}
      </span>
    );
  };

  const formatSeconds = (seconds) => {
    const total = Number(seconds || 0);
    const hrs = Math.floor(total / 3600);
    const mins = Math.floor((total % 3600) / 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  if (loading) {
    return (
      <div className={`min-h-screen px-4 py-8 ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
        <div className="flex h-96 items-center justify-center">
          <p className={isDarkMode ? "text-slate-400" : "text-slate-600"}>Loading student data...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={`flex flex-wrap items-center gap-2 border-b px-6 ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-gray-100 bg-white"}`}>
        {[
          { key: "metrics", label: "Metrics" },
          { key: "activity", label: "Activity" },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveView(tab.key)}
            className={`rounded-t-md border px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
              activeView === tab.key
                ? isDarkMode
                  ? "border-sky-500/70 bg-sky-500/15 text-sky-200 shadow-[0_0_0_1px_rgba(56,189,248,0.25)]"
                  : "border-sky-300 bg-sky-50 text-sky-700 shadow-[0_0_0_1px_rgba(14,165,233,0.22)]"
                : isDarkMode
                  ? "border-transparent text-slate-400 hover:bg-slate-800/70 hover:text-slate-200"
                  : "border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={`border-x border-b p-6 shadow-lg ${isDarkMode ? "border-slate-800 bg-slate-900 shadow-black/20" : "border-slate-200 bg-white shadow-slate-200/60"}`}>
        {activeView === "metrics" && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            {[
              { label: "Total Students", value: students.length, color: "from-blue-600 to-blue-400" },
              { label: "Active Now", value: students.filter((s) => s.usageStatus === "active").length, color: "from-green-600 to-green-400" },
              { label: "Inactive", value: students.filter((s) => s.usageStatus === "inactive").length, color: "from-yellow-600 to-yellow-400" },
              { label: "No Usage", value: students.filter((s) => s.usageStatus === "unused").length, color: "from-red-600 to-red-400" },
            ].map((stat, idx) => (
              <div key={idx} className={`overflow-hidden rounded-xl border ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
                <div className={`h-1 bg-gradient-to-r ${stat.color}`} />
                <div className="p-4">
                  <p className={`text-xs font-semibold uppercase ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>{stat.label}</p>
                  <p className={`mt-2 text-2xl font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>{stat.value}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeView === "activity" && (
          <>
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <FaSearch className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-slate-600" : "text-slate-400"}`} />
                <input
                  type="text"
                  placeholder="Search by student name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full rounded-lg border px-4 py-2.5 pl-10 transition focus:outline-none focus:ring-2 ${
                    isDarkMode
                      ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500 focus:ring-blue-500/20"
                      : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus:ring-blue-500/20"
                  }`}
                />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className={`rounded-lg border px-4 py-2.5 transition focus:outline-none focus:ring-2 ${
                  isDarkMode
                    ? "border-slate-700 bg-slate-950 text-white focus:ring-blue-500/20"
                    : "border-slate-300 bg-white text-slate-900 focus:ring-blue-500/20"
                }`}
              >
                <option value="all">All Students</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="unused">No Usage</option>
              </select>
            </div>

            <div className={`overflow-hidden rounded-2xl border shadow-lg ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <div className={`border-b px-6 py-4 ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                <h2 className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                  Students ({filteredStudents.length})
                </h2>
              </div>

              {filteredStudents.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={isDarkMode ? "bg-slate-800/50" : "bg-slate-50"}>
                      <tr>
                        <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Student</th>
                        <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Status</th>
                        <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Today</th>
                        <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>This Week</th>
                        <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>Last Active</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode ? "divide-slate-800" : "divide-slate-200"}`}>
                      {filteredStudents.map((student) => (
                        <tr
                          key={student.id}
                          onClick={() => setSelectedStudent(student)}
                          className={`cursor-pointer transition ${isDarkMode ? "hover:bg-slate-800/50" : "hover:bg-slate-50"}`}
                        >
                          <td className={`px-6 py-4 text-sm font-semibold ${isDarkMode ? "text-white" : "text-slate-900"}`}>{student.firstName} {student.lastName}</td>
                          <td className="px-6 py-4">{getStatusBadge(student.usageStatus)}</td>
                          <td className={`px-6 py-4 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>{formatSeconds(student.totalTimeToday)}</td>
                          <td className={`px-6 py-4 text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>{formatSeconds(student.totalTimeThisWeek)}</td>
                          <td className={`px-6 py-4 text-xs ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>
                            {student.lastActive ? `${new Date(student.lastActive).toLocaleDateString()} ${new Date(student.lastActive).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-6 py-8 text-center">
                  <p className={isDarkMode ? "text-slate-500" : "text-slate-600"}>No students found matching your criteria</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {selectedStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className={`max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl ${isDarkMode ? "bg-slate-900" : "bg-white"}`}>
            <div className={`sticky top-0 border-b px-6 py-4 ${isDarkMode ? "border-slate-800 bg-slate-800/50" : "border-slate-200 bg-slate-50"}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                  {selectedStudent.firstName} {selectedStudent.lastName}
                </h2>
                <button onClick={() => setSelectedStudent(null)} className="text-2xl font-bold text-slate-500 hover:text-slate-700">x</button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {[
                  { label: "Status", value: selectedStudent.usageStatus },
                  { label: "Today", value: formatSeconds(selectedStudent.totalTimeToday) },
                  { label: "This Week", value: formatSeconds(selectedStudent.totalTimeThisWeek) },
                  { label: "This Month", value: formatSeconds(selectedStudent.totalTimeThisMonth) },
                ].map((item, idx) => (
                  <div key={idx} className={`rounded-lg border p-4 ${isDarkMode ? "border-slate-800 bg-slate-800/50" : "border-slate-200 bg-slate-50"}`}>
                    <p className={`text-xs font-semibold uppercase ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>{item.label}</p>
                    <p className={`mt-2 text-lg font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>{item.value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <h3 className={`mb-4 text-sm font-bold uppercase ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
                  Most Used Features
                </h3>
                <div className="space-y-2">
                  {selectedStudent.topFeatures?.map((feature, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className={`text-sm ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>{feature.name}</span>
                      <span className={`text-xs font-semibold ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>{feature.visits} visits</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
